/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class FriendsSaleCountdown extends HTMLElement {
  connectedCallback() {
    this.endAt = this.getEndAt();

    if (Number.isNaN(this.endAt.getTime())) {
      const showEditorWarning = Boolean(window.Shopify?.designMode);
      this.textContent = showEditorWarning ? 'Check sale end date' : '';
      this.hidden = !showEditorWarning;
      return;
    }

    this.hidden = false;
    this.update();
    this.interval = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  getEndAt() {
    const year = Number(this.getAttribute('end-year'));
    const month = Number(this.getAttribute('end-month'));
    const day = Number(this.getAttribute('end-day'));
    const timeZone = this.getAttribute('time-zone');

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12 || day < 1 || day > 31 || !timeZone) {
      return new Date(Number.NaN);
    }

    const calendarCheck = new Date(Date.UTC(year, month - 1, day));

    if (calendarCheck.getUTCFullYear() !== year || calendarCheck.getUTCMonth() !== month - 1 || calendarCheck.getUTCDate() !== day) {
      return new Date(Number.NaN);
    }

    const intendedWallClock = Date.UTC(year, month - 1, day, 23, 59, 59);
    let targetTimestamp = intendedWallClock;

    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      // Two passes account for a daylight-saving offset changing between the
      // initial UTC guess and the requested local deadline.
      for (let pass = 0; pass < 2; pass += 1) {
        const parts = Object.fromEntries(
          formatter.formatToParts(new Date(targetTimestamp))
            .filter(({ type }) => type !== 'literal')
            .map(({ type, value }) => [type, Number(value)])
        );
        const renderedWallClock = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

        targetTimestamp += intendedWallClock - renderedWallClock;
      }
    } catch (_error) {
      return new Date(Number.NaN);
    }

    return new Date(targetTimestamp);
  }

  update() {
    const remainingSeconds = Math.max(0, Math.floor((this.endAt.getTime() - Date.now()) / 1000));
    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    const clock = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');

    this.textContent = days > 0 ? `${days}d ${clock}` : clock;

    if (remainingSeconds === 0) {
      window.clearInterval(this.interval);
    }
  }
}

if (!window.customElements.get('sale-countdown')) {
  window.customElements.define('sale-countdown', FriendsSaleCountdown);
}

class FriendsQuantityLimits {
  constructor() {
    this.cartQuantities = new Map();
    this.readInitialQuantities();

    document.addEventListener('submit', (event) => this.onProductSubmit(event), true);
    document.documentElement.addEventListener('cart:updated', (event) => this.onCartUpdated(event));
    document.addEventListener('shopify:section:load', () => this.updateControls());

    this.updateControls();
  }

  readInitialQuantities() {
    document.querySelectorAll('[data-friends-quantity-limit-marker]').forEach((marker) => {
      const productId = marker.dataset.friendsProductId;
      const cartQuantity = Number(marker.dataset.friendsCartQuantity || 0);

      if (productId) {
        this.cartQuantities.set(productId, cartQuantity);
      }
    });
  }

  onProductSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') {
      return;
    }

    const marker = form.querySelector('[data-friends-quantity-limit-marker]');

    if (!marker) {
      return;
    }

    const productId = marker.dataset.friendsProductId;
    const limit = Number(marker.dataset.friendsQuantityLimit);
    const quantityInput = form.elements.namedItem('quantity');
    const requestedQuantity = Number(quantityInput?.value || 1);
    const cartQuantity = this.cartQuantities.get(productId) ?? Number(marker.dataset.friendsCartQuantity || 0);
    const remainingQuantity = Math.max(0, limit - cartQuantity);

    if (requestedQuantity <= remainingQuantity) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: 'error',
        error: marker.dataset.friendsLimitMessage || `Maximum ${limit} per order.`
      }
    }));
  }

  onCartUpdated(event) {
    const cart = event.detail?.cart;

    if (!cart?.items) {
      return;
    }

    this.cartQuantities = cart.items.reduce((quantities, item) => {
      const productId = String(item.product_id);
      quantities.set(productId, (quantities.get(productId) || 0) + item.quantity);
      return quantities;
    }, new Map());

    this.updateControls();
  }

  updateControls() {
    document.querySelectorAll('[data-friends-quantity-limit-marker]').forEach((marker) => {
      const productId = marker.dataset.friendsProductId;
      const limit = Number(marker.dataset.friendsQuantityLimit);
      const cartQuantity = this.cartQuantities.get(productId) ?? Number(marker.dataset.friendsCartQuantity || 0);
      const remainingQuantity = Math.max(0, limit - cartQuantity);
      const form = marker.closest('form');

      marker.dataset.friendsCartQuantity = String(cartQuantity);

      if (!form) {
        return;
      }

      const quantityInput = form.elements.namedItem('quantity');

      if (quantityInput instanceof HTMLInputElement && quantityInput.type === 'number') {
        const nativeMax = Number(quantityInput.dataset.friendsNativeMax || Number.MAX_SAFE_INTEGER);
        quantityInput.max = String(Math.min(nativeMax, Math.max(1, remainingQuantity)));
        quantityInput.value = String(Math.min(Number(quantityInput.value || 1), Number(quantityInput.max)));
      }

      form.querySelectorAll('[data-product-add-to-cart-button]').forEach((button) => {
        if (remainingQuantity === 0) {
          button.disabled = true;
          button.setAttribute('data-friends-limit-disabled', '');
        } else if (button.hasAttribute('data-friends-limit-disabled')) {
          button.disabled = false;
          button.removeAttribute('data-friends-limit-disabled');
        }
      });
    });
  }
}

new FriendsQuantityLimits();
